import IconButton from '@eduzz/houston-ui/IconButton';
import List from '@eduzz/houston-ui/List';
import Typography from '@eduzz/houston-ui/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import React, { memo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useObservable, useCallbackObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import HelperPage from 'v2/components/Shared/HelperPage';
import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import configAPIService from 'v2/services/configAPI';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { NewApiKeyDialog } from './newApiKeyDialog';
import styles from './styles';

export const ConfigAPI = memo(() => {
  const classes = styles();

  useObservable(() => configAPIService.loadApiKey(), []);
  useObservable(() => configAPIService.loadOriginKey(), []);

  const [showApiKey, setShowApiKey] = useState<boolean>(false);

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const [loadingApiKey] = useObservable(() => configAPIService.isLoadingApiKey(), []);
  const [apiKey] = useObservable(() => configAPIService.getApiKey(), []);

  const [loadingOriginKey] = useObservable(() => configAPIService.isLoadingOriginKey(), []);
  const [originKey] = useObservable(() => configAPIService.getOriginKey(), []);

  const [open, setOpen] = useState<boolean>(false);

  const [updateOriginKey] = useCallbackObservable(
    () =>
      configAPIService.updateOriginKey().pipe(
        tap(() => {
          toast.success('API key atualizada com sucesso');
        }),
        catchError(() => {
          toast.error('Ocorreu um erro ao tentar gerar uma nova Origin Key');
          return of(true);
        })
      ),
    []
  );

  const onClose = () => {
    setShowApiKey(true);
    setOpen(false);
  };

  useEffect(() => () => setShowApiKey(false), []);

  const getShowKey = () => {
    if (apiKey?.deprecated_api_key) return apiKey.deprecated_api_key;
    if (showApiKey && apiKey?.api_key) return apiKey?.api_key;
    if (apiKey?.api_key_last_digits) return `*********${apiKey?.api_key_last_digits}`;
    return '*************';
  };

  const currentApiKey = getShowKey();

  const listKeys = [
    {
      name: 'Public Key',
      key: `${user?.id}`,
      canCopy: true,
      generated_at: '',
      loading: false,
    },
    {
      name: 'API Key',
      key: currentApiKey,
      canCopy: showApiKey,
      generated_at: apiKey?.api_key_generated_at,
      loading: loadingApiKey,
    },
    {
      name: 'Origin Key',
      key: originKey?.origin_secret ?? 'Não gerada',
      canCopy: !!originKey?.origin_secret,
      generated_at: originKey?.origin_secret_generated_at,
      loading: loadingOriginKey,
    },
  ];

  return (
    <>
      <div className={classes.api}>
        <div className={classes.head}>
          <div className={classes.title}>
            <h1>API Eduzz</h1>
            <p>Gerencie suas chaves de integração</p>
          </div>
          <HelperPage id='config-api-button' title='Configuração de API' name='config_api' />
        </div>
        <div className={classes.apiContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <List>
                {listKeys.map((key) => (
                  <List.Item key={key.key}>
                    <List.Text
                      title={<div className={classes.listTitle}>{key.name}</div>}
                      description={
                        <>
                          {key.loading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Typography size='xxs'>
                              <Truncate count={40} final='...' title={key.key}>
                                {key.key}
                              </Truncate>
                            </Typography>
                          )}
                          {!key.loading && key.generated_at && (
                            <div className={classes.createdAt}>Criado em: {dateFormat(key.generated_at, 'dd/MM/yyyy HH:mm')}</div>
                          )}
                        </>
                      }
                    />
                    {key.canCopy && (
                      <List.Right>
                        <IconButton onClick={() => navigator.clipboard.writeText(key.key)}>
                          <FileCopyIcon />
                        </IconButton>
                      </List.Right>
                    )}
                  </List.Item>
                ))}
              </List>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.info}>* Não compartilhe essas informações com ninguém!</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                startIcon={loadingOriginKey ? <CircularProgress size={16} color='inherit' /> : <RefreshIcon />}
                fullWidth
                variant='contained'
                color='primary'
                disabled={loadingOriginKey}
                onClick={updateOriginKey}
              >
                Gerar nova Origin key
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                startIcon={loadingApiKey ? <CircularProgress size={16} color='inherit' /> : <RefreshIcon />}
                fullWidth
                variant='contained'
                color='primary'
                disabled={loadingApiKey}
                onClick={() => setOpen(true)}
              >
                Gerar nova API key
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
      <NewApiKeyDialog open={open} onClose={onClose} onConfirm={onClose} />
    </>
  );
});
